import { Music, Video, Play, Users, Zap } from 'lucide-react';
import { getProfilePicUrl } from '../utils/profilePic';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_ARTWORK = 'https://customer-assets.emergentagent.com/job_aa2895d0-4baa-4f76-9868-0fba7ba8ce95/artifacts/0pzzzles_7B6FD963-E98B-48F6-BF03-DF4A8E2A8307.jpeg';

// Resolve URL — never return direct R2/S3 URLs to avoid ORB blocking
const resolveUrl = (url, proxyPath) => {
  if (!url) return null;
  // Already a proxy API path
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // External non-R2 CDN URLs (customer-assets, pexels, etc.) are safe
  if (url.startsWith('http') && !url.includes('r2.cloudflarestorage.com') && !url.includes('r2.dev')) return url;
  // R2 URL or other storage — route through proxy
  if (url.startsWith('http')) {
    const filename = url.split('/').pop()?.split('?')[0];
    return filename ? `${BACKEND_URL}${proxyPath}/${filename}` : null;
  }
  // Filename — construct proxy path
  return `${BACKEND_URL}${proxyPath}/${url}`;
};

function GoldFrameCard({ type = 'track', item, onClick }) {
  const isTrack = type === 'track';
  const isVideo = type === 'video';
  const isProfile = type === 'profile';

  const getImage = () => {
    if (isProfile) {
      return getProfilePicUrl(item.profile_picture, item.id || item.user_id);
    }
    if (isVideo) {
      return item.thumbnail_url 
        ? resolveUrl(item.thumbnail_url, '/api/thumbnail')
        : (item.thumbnail ? `${BACKEND_URL}/api/thumbnail/${item.thumbnail}` : null);
    }
    const art = item.artwork_url || item.cover_url || item.artwork;
    if (!art) return DEFAULT_ARTWORK;
    return resolveUrl(art, '/api/artwork');
  };

  const getTitle = () => {
    if (isProfile) return item.username;
    return item.title || 'Untitled';
  };

  const getSubtitle = () => {
    if (isProfile) return `${item.follower_count || item.followers_count || 0} followers`;
    return item.artist || 'Unknown';
  };

  const getPrice = () => {
    if (isProfile) return null;
    if (item.is_free) return 'FREE';
    if (item.price) return `$${item.price}`;
    return null;
  };

  const imageUrl = getImage();
  const price = getPrice();

  return (
    <div
      className="gold-frame-card group cursor-pointer"
      onClick={onClick}
      data-testid={`gold-frame-${type}-${item.id}`}
    >
      {/* Ornate outer frame */}
      <div className="gold-frame-outer">
        {/* Corner ornaments */}
        <div className="gold-corner gold-corner-tl" />
        <div className="gold-corner gold-corner-tr" />
        <div className="gold-corner gold-corner-bl" />
        <div className="gold-corner gold-corner-br" />

        {/* Inner frame with content */}
        <div className="gold-frame-inner">
          {/* Image area */}
          <div className="gold-frame-image">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={getTitle()}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex'); }}
              />
            ) : null}
            <div
              className="w-full h-full items-center justify-center bg-[#111]"
              style={{ display: imageUrl ? 'none' : 'flex' }}
            >
              {isProfile ? (
                <span className="text-[#d4a843] text-3xl font-bold font-['Chakra_Petch']">
                  {item.username?.[0]?.toUpperCase() || '?'}
                </span>
              ) : isVideo ? (
                <Video size={32} className="text-[#d4a843]/40" />
              ) : (
                <Music size={32} className="text-[#d4a843]/40" />
              )}
            </div>

            {/* Overlay badges */}
            {isVideo && (
              <div className="absolute top-2 right-2 bg-black/70 px-1.5 py-0.5 flex items-center gap-1">
                <Play size={10} className="text-[#d4a843]" />
                <span className="text-[#d4a843] text-[9px] font-mono">VIDEO</span>
              </div>
            )}
            {/* iter_471 — Gold "PROMOTED" pill rendered on any card whose
                backing item carries the is_promoted flag (tracks, videos,
                beats, AI tracks). Tiny + corner-anchored so it doesn't
                fight the existing FREE/price/video badges. */}
            {item?.is_promoted && (
              <div
                className="absolute top-2 left-2 px-1.5 py-0.5 bg-[#FFDA33] text-black font-mono text-[9px] uppercase font-bold flex items-center gap-0.5 shadow-lg"
                data-testid={`promoted-badge-${item.id}`}
                title="Currently being promoted by the creator"
              >
                <Zap size={9} /> Promoted
              </div>
            )}
            {price && (
              <div className="absolute bottom-2 right-2 bg-black/80 border border-[#d4a843]/40 px-2 py-0.5">
                <span className="text-[#d4a843] text-[10px] font-mono font-bold">{price}</span>
              </div>
            )}

            {/* Hover play icon */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="w-10 h-10 bg-[#d4a843] flex items-center justify-center">
                {isProfile ? <Users size={18} className="text-black" /> : <Play size={18} className="text-black ml-0.5" />}
              </div>
            </div>
          </div>

          {/* Text area */}
          <div className="gold-frame-text">
            <h3 className="gold-frame-title">{getTitle()}</h3>
            <p className="gold-frame-subtitle">{getSubtitle()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GoldFrameCard;
